import { IObservable, Subject } from '~/rage/utils';
import { IModalReply, IModalState } from '~/shared';
import { IModalService } from './modalServiceTypes';

type ModalCallback = (result: IModalReply<IModalState>) => void;

export class AbstractModalService implements IModalService {
	private readonly modal = new Subject<IModalState | null>(null);
	private isTransitioningModal = false;
	private activeCallback: ModalCallback | null = null;

	private readonly modalQueue: {
		options: IModalState;
		resolve: ModalCallback;
	}[] = [];

	public getCurrentModal(): IObservable<IModalState | null> {
		return this.modal;
	}

	public replyToCurrentModal<T extends IModalState>(
		result: IModalReply<T>,
	): void {
		if (this.activeCallback) {
			this.activeCallback(result);
		}

		this.clearCurrentModal();
	}

	public showModal<T extends IModalState>(
		options: T,
		callback: (result: IModalReply<T>) => void,
	): void {
		if (this.modal.getValue() !== null || this.isTransitioningModal) {
			this.modalQueue.push({ options, resolve: callback as ModalCallback });
		} else {
			this.modal.next(options);
			this.activeCallback = callback as ModalCallback;
		}
	}

	public showModalAsync<T extends IModalState>(
		options: T,
	): Promise<IModalReply<T>> {
		if (this.modal.getValue() !== null || this.isTransitioningModal) {
			return new Promise<IModalReply<T>>((resolve) => {
				this.modalQueue.push({
					options,
					resolve: resolve as unknown as ModalCallback,
				});
			});
		} else {
			this.modal.next(options);
			return new Promise<IModalReply<T>>((resolve) => {
				this.activeCallback = resolve as unknown as ModalCallback;
			});
		}
	}

	private clearCurrentModal() {
		this.modal.next(null);
		this.activeCallback = null;
		this.processNextModal();
	}

	private processNextModal() {
		if (this.modalQueue.length > 0 && !this.isTransitioningModal) {
			this.isTransitioningModal = true;
			setTimeout(() => {
				const { options, resolve } = this.modalQueue.shift()!;
				this.modal.next(options);
				this.activeCallback = resolve;
				this.isTransitioningModal = false;
			}, 200);
		}
	}
}
